import React from "react";
import { css } from "@emotion/css";
import { IoLocationOutline } from "react-icons/io5";
import { SectionCard } from "../../../../shared/ui/section-card/section-card";
import { SectionTitle } from "../../../../shared/ui/section-title/section-title";
import type { EventImage } from "../../../../api/services/events/types";

export interface EventLocationCardProps extends React.HTMLAttributes<HTMLDivElement> {
  theaterName: string;
  theaterDescription?: string;
  theaterImage?: EventImage;
}

const sectionHeader = css({
  display: "flex",
  alignItems: "center",
  gap: "var(--spacing-sm)",
  marginBottom: "var(--spacing-lg)",
  position: "relative",
  paddingLeft: "var(--spacing-sm)",
});

const sectionIcon = css({
  color: "var(--pc-700)",
  minWidth: "20px",
  width: "20px",
  height: "20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const content = css({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  gap: "1.5rem",
  paddingLeft: "var(--spacing-md)",
  paddingRight: "var(--spacing-md)",

  "@media (max-width: 768px)": {
    paddingLeft: "var(--spacing-sm)",
    paddingRight: "var(--spacing-sm)",
  },

  "@media (max-width: 48rem)": {
    flexDirection: "column",
  },
});

const info = css({
  flex: 1,
  paddingRight: "1.5rem",
  
  "@media (max-width: 48rem)": {
    paddingRight: 0,
  },
});

const theaterName = css({
  fontSize: "var(--fs-500)",
  color: "var(--pc-700)",
  marginBottom: "var(--spacing-xs)",
  fontWeight: 600,
  
  "@media (max-width: 768px)": {
    fontSize: "var(--fs-450)",
  },
  
  "@media (max-width: 480px)": {
    fontSize: "var(--fs-400)",
  },
});

const theaterDescription = css({
  lineHeight: 1.6,
  color: "var(--pc-700)",
  fontSize: "var(--fs-350)",
  
  "@media (max-width: 48rem)": {
    fontSize: "0.9375rem",
  },
  
  "@media (max-width: 30rem)": {
    fontSize: "0.875rem",
  },
});

const theaterImageContainer = css({
  flex: 1,
  width: "100%",
  minWidth: "17.5rem",
  maxWidth: "28.125rem",
  borderRadius: "var(--spacing-md)",
  overflow: "hidden",
  
  "@media (max-width: 48rem)": {
    maxWidth: "100%",
  },
});

const theaterImage = css({
  width: "100%",
  height: "auto",
  objectFit: "cover",
  borderRadius: "var(--spacing-md)",
  aspectRatio: "16/9",
});

export const EventLocationCard: React.FC<EventLocationCardProps> = ({
  theaterName: name,
  theaterDescription: description,
  theaterImage: image,
  className,
  ...rest
}) => {
  const resolveImageUrl = (): string | undefined => {
    if (!image) return undefined;

    // Build from name + extension
    if (image.name && image.extension) {
      const ext = image.extension.startsWith('.')
        ? image.extension
        : `.${image.extension}`;
      return `/assets/${image.name}${ext}`;
    }

    // Fallback to url if present
    if (image.url) return image.url;

    return undefined;
  };

  const imageUrl = resolveImageUrl();
  const placeholderUrl = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="450"%3E%3Crect width="800" height="450" fill="%23e5e7eb"/%3E%3C/svg%3E';

  return (
    <SectionCard className={className} {...rest}>
      <div className={sectionHeader}>
        <IoLocationOutline className={sectionIcon} />
        <SectionTitle level={2}>Local do evento</SectionTitle>
      </div>
      
      <div className={content}>
        <div className={info}>
          <h3 className={theaterName}>{name}</h3>
          
          {description && (
            <p className={theaterDescription}>{description}</p>
          )}
        </div>
        
        {(imageUrl || image) && (
          <div className={theaterImageContainer}>
            <img
              src={imageUrl || placeholderUrl}
              alt={name}
              className={theaterImage}
            />
          </div>
        )}
      </div>
    </SectionCard>
  );
};

export default EventLocationCard;
